/**
 * useCampaignReportingWindow — makes the reporting window (1 Week / 1 Month /
 * 6 Month / Full Campaign) driven by the `?win` search param, shared by both
 * windowed reporting sub-tabs (General Insights, Sentiment Intelligence) —
 * mirrors useCampaignReportingDeepLink's URL-ownership pattern for `?sub`.
 *
 * An absent `?win` resolves to `DEFAULT_TIME_PERIOD`. `setWindow` always
 * writes the value it is given, `'full'` included, so a chosen Full Campaign
 * stays distinguishable from "nothing chosen" (the share grant's `window`
 * field makes the same distinction).
 */
import { useCallback } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { CampaignSearch } from './search';
import { DEFAULT_TIME_PERIOD, type TimePeriod } from '../campaign-reporting/reportingWindow';

export function useCampaignReportingWindow(): {
  window: TimePeriod;
  setWindow: (next: TimePeriod) => void;
} {
  const { win } = useSearch({ strict: false }) as Partial<CampaignSearch>;
  const navigate = useNavigate();


  const setWindow = useCallback(
    (next: TimePeriod) =>
      navigate({
        to: '.',
        search: (prev: Partial<CampaignSearch>) => ({ ...prev, win: next }),
      } as never),
    [navigate],
  );

  return { window: win ?? DEFAULT_TIME_PERIOD, setWindow };
}
